import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ShoppingCart, Check } from "lucide-react";
import { BOOKS } from "../data/content";
import { BookCard } from "../components/BookCard";
import { Reveal } from "../components/Reveal";

export default function BookDetail() {
  const { id } = useParams();
  const book = BOOKS.find((b) => b.id === id);
  if (!book) return <Navigate to="/books" replace />;

  const others = BOOKS.filter((b) => b.id !== id).slice(0, 3);
  const specs = [
    ["Author", book.author],
    ["Publisher", "DatAsia Press"],
    ["Year", book.year],
    ["Format", book.format],
    ["Extent", book.pages],
    ["Language", "English"],
  ];

  return (
    <div data-testid="book-detail-page" className="pt-28 lg:pt-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
        <Link
          to="/books"
          data-testid="back-to-books"
          className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--text-2)] hover:text-[var(--text)] transition-colors"
        >
          <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
          All Books
        </Link>

        <div className="mt-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="lg:sticky lg:top-28">
              <div className="overflow-hidden rounded-sm bg-[var(--surface-2)] deckle">
                <img
                  src={book.cover}
                  alt={book.title}
                  className="w-full object-cover"
                />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <span className="font-mono text-xs uppercase tracking-[0.26em] text-[var(--gold)]">
              DatAsia Edition · {book.year}
            </span>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text)]">
              {book.title}
            </h1>
            <p className="mt-3 font-serif italic text-xl text-[var(--text-2)]">
              {book.subtitle}
            </p>

            <p className="mt-7 font-sans text-base sm:text-lg leading-relaxed text-[var(--text-2)]">
              {book.blurb}
            </p>

            <div className="mt-7 space-y-2.5">
              {[
                "Fully edited and annotated for the modern reader",
                "Printed on archival paper with maps and illustrations",
                "Part of the DatAsia Southeast Asia heritage series",
              ].map((line) => (
                <div key={line} className="flex items-start gap-3">
                  <Check className="h-4 w-4 mt-1 text-[var(--terracotta)] shrink-0" />
                  <span className="font-sans text-sm text-[var(--text-2)]">{line}</span>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <a
                href={book.amazon}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="buy-on-amazon-button"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[var(--terracotta)] text-[#F9F6F0] px-8 py-4 font-sans text-sm font-semibold hover:bg-[var(--ink)] transition-colors duration-300"
              >
                <ShoppingCart className="h-4 w-4" />
                Buy on Amazon
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>

            <dl className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-y-6 gap-x-4 border-t border-[var(--border-c)] pt-8">
              {specs.map(([k, v]) => (
                <div key={k}>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--text-2)]">
                    {k}
                  </dt>
                  <dd className="mt-1.5 font-sans text-sm text-[var(--text)] font-medium">
                    {v}
                  </dd>
                </div>
              ))}
            </dl>
          </motion.div>
        </div>

        <div className="mt-24 lg:mt-32 pb-24 lg:pb-32">
          <h2 className="font-serif text-2xl sm:text-3xl text-[var(--text)]">
            More from the collection
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-14">
            {others.map((b, i) => (
              <Reveal key={b.id} delay={i * 0.08}>
                <BookCard book={b} />
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
